import { EntityId } from '@reduxjs/toolkit';
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { differenceBy, intersectionBy, unionBy } from 'lodash';
import keyBy from 'lodash/keyBy';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import {
  ProjectId,
  Team,
  User,
  UserStatus,
  Order,
  OrderType,
  UserType,
} from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Project, ProjectWithPartners } from 'api/project';
import { ProjectInUserProfile } from 'api/user';
import { CustomOption } from 'components/atoms/select';
import type { Views } from 'components/molecules/views/types';
import type { FormResponse } from 'components/organisms/forms/form-types';
import { ColumnOrder } from 'components/organisms/grid-custom-column';
import { MembersAPI } from 'data/members/api';
import { TemplateId, TemplateObjectType } from 'data/templates/api';
import { DocumentTemplate } from 'data/templates/documents';
import { ProjectTemplateApi } from 'data/templates/projects';
import { useUser } from 'hooks/use-user';
import {
  useProjectMembersQuery,
  useProjectPartnersMappingQuery,
} from 'queries';
import { useProjectsFromInitData } from 'queries/account';
import { AppUpgradeConfirmationKey } from 'queries/app-upgrade-confirmation';
import { ApiErrorData } from 'queries/error';
import {
  useMembersV2TransformedQuery,
  useTeamsTransformedQuery,
} from 'queries/members';
import { useProjectDetailQuery } from 'queries/project';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { unreachable } from 'utils/unreachable';
import { db } from 'workers';
import { migrations } from 'workers/migrations';
import { FormResponseDraft } from 'workers/models/form-responses';
import { TemplateDraft } from 'workers/models/project-templates';
import { useProjectAsRBACResource, useWithAccess } from './rbac/useWithAccess';

export const useInitIndexDbListeners = () => {
  const queryClient = useQueryClient();

  db.on('ready', (e) => {
    logger.log('db ready with version', e?.verno);
  });

  db.on('close', (e) => {
    logger.log('db close event fired', e);
  });

  db.on('versionchange', (e) => {
    /**
     * versionchange event is fired in any open windows when a new window is opened which connects to a different version of indexD
     */
    logger.log('db versionchange', e);

    queryClient.setQueryData<boolean>(AppUpgradeConfirmationKey, true);
  });
};

const filterNonCustomerUsers = (user) => {
  return user.userType !== UserType.External;
};

// export function usePrerenderMembersAndTeamsFromIndexedDb(): void {
//   const { data: membersData } = useMembersV2TransformedQuery();

//   const members = membersData?.users ?? [];

//   const teams = membersData?.teams ?? [];

//   activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
//     users: members || [],
//     teams: teams || [],
//   };
// }

/**
 * @deprecated
 */
export function useGetAllNativeUsersFromIndexedDb(): User[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).filter(
      (x) => x.userType === UserType.Native,
    );

    return data;
  }, [membersData?.users]);
}

/**
 * @deprecated
 */
export function useGetAllActiveNativeUsersFromIndexedDb(): {
  users: User[];
  isLoading: boolean;
} {
  const { data: membersData, isLoading: isMembersDataLoading } =
    useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).filter(
      (x) =>
        x.userType === UserType.Native && x.userStatus !== UserStatus.Inactive,
    );

    return { users: data, isLoading: isMembersDataLoading };
  }, [membersData?.users, isMembersDataLoading]);
}

/**
 * @deprecated
 */
export function useGetAllActivePartnerUsersFromIndexedDb(): User[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).filter(
      (x) => x.userType === UserType.Partner,
    );

    return data;
  }, [membersData?.users]);
}

export function useGetActiveOrInvitedNonCustomerUsersFromIndexedDb(): User[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).filter(filterNonCustomerUsers);

    return data;
  }, [membersData?.users]);
}

/**
 * @deprecated
 */
export function useGetUserByUserId(userId?: number): User | undefined {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    if (!userId) {
      return undefined;
    }
    const data = (membersData?.users ?? []).find((x) => x.userId === userId);

    return data;
  }, [membersData?.users, userId]);
}

export function useGetTeamsByProjectId(
  projectIds: number[],
  enabled = true,
): {
  data: Record<string, Team[]>;
  isLoading: boolean;
} {
  const { data: membersData, isLoading: isMembersDataLoading } =
    useMembersV2TransformedQuery();

  const getData = useCallback(() => {
    const teams = membersData?.teams;

    if (enabled === false) {
      return undefined;
    }
    const teamsByProjectIds: Record<string, Team[]> = {};

    projectIds.forEach((projectId) => {
      const teamList = (teams ?? []).filter((t) =>
        t.projectIds?.includes?.(projectId),
      );

      teamsByProjectIds[projectId] = teamList;
    });

    return teamsByProjectIds;
  }, [enabled, membersData?.teams, projectIds]);

  return useMemo(() => {
    const data = getData();

    return data != null
      ? { data, isLoading: isMembersDataLoading }
      : { data: {}, isLoading: true };
  }, [getData]);
}

export function useGetTeamsByProjectIdV2(): {
  data: Record<string, Team[]>;
  isLoading: boolean;
} {
  const { data: teams, isLoading: isTeamsDataLoading } =
    useTeamsTransformedQuery();

  const getData = useCallback(() => {
    const teamsByProjectIds: Record<string, Team[]> = {};

    (teams ?? []).forEach((team) => {
      (team.projectIds ?? []).forEach((projectId) => {
        if (!teamsByProjectIds[projectId]) {
          teamsByProjectIds[projectId] = [];
        }

        teamsByProjectIds[projectId].push(team);
      });
    });

    return teamsByProjectIds;
  }, [teams]);

  return useMemo(() => {
    const data = getData();

    return data != null
      ? { data, isLoading: isTeamsDataLoading }
      : { data: {}, isLoading: true };
  }, [getData]);
}

// having this to avoid circular dependency
export const useProjectDetail = (
  projectId: ProjectId,
  options: UseQueryOptions<Project, ApiErrorData, Project, ProjectId> = {},
) => {
  return useProjectDetailQuery(projectId, {
    ...options,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: typeof projectId?.[1] === 'number' && projectId?.[1] !== -1,
  });
};

interface ProjectMembersOptions {
  includeRestricedPartnersMembers?: boolean;
  dontIncludeCustomers?: boolean;
}

export function useGetAllMembersInProject({
  isPrivate = false,
  projectId,
  options = {
    includeRestricedPartnersMembers: true,
  },
}: {
  projectId?: number;
  isPrivate?: boolean;
  options?: ProjectMembersOptions;
}): { data: User[]; isLoading: boolean } {
  const { data: project } = useProjectDetail(ProjectId(projectId ?? -1));
  const projectMembers = useProjectMembersQuery([
    'project_id',
    projectId ?? -1,
  ]);
  const membersData = useMemo(
    () => ({
      users: projectMembers?.data?.userList ?? [],
      teams: projectMembers?.data?.teamList ?? [],
    }),
    [projectMembers],
  );

  const permissivePartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          (partner) =>
            partner.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map((partner) => partner.companyId),
    [project?.partners],
  );
  const restricedPartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          (partner) =>
            partner.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map((partner) => partner.companyId),
    [project?.partners],
  );
  const filterForPrivateResource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      ((user.userType === UserType.Partner ||
        user.userType === UserType.CustomerPartner) &&
        permissivePartnerCompanyIdsInProject.includes(user.company.companyId)),
    [permissivePartnerCompanyIdsInProject],
  );
  const filterOutRestrictedPartners = useCallback(
    (user: User) => {
      return (
        user.userType !== UserType.CustomerPartner ||
        user.userType !== UserType.CustomerPartner ||
        !restricedPartnerCompanyIdsInProject.includes(user.company.companyId)
      );
    },
    [restricedPartnerCompanyIdsInProject],
  );
  const conditionalFilter = useCallback(
    (user: User) => {
      return (
        (options.includeRestricedPartnersMembers === true ||
          filterOutRestrictedPartners(user)) &&
        (isPrivate ? filterForPrivateResource(user) : true)
      );
    },
    [
      filterForPrivateResource,
      filterOutRestrictedPartners,
      isPrivate,
      options.includeRestricedPartnersMembers,
    ],
  );

  const data = useMemo(() => {
    if (!projectId) {
      return [];
    }

    return (membersData?.users ?? []).filter(conditionalFilter);
  }, [projectId, membersData, conditionalFilter]);

  return { data: data || [], isLoading: projectMembers.isLoading };
}

/**
 * @deprecated
 */
export function useGetAllUsers({
  includeDeactivatedUsers = false,
}: {
  includeDeactivatedUsers?: boolean;
}): User[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).filter((user) => {
      return includeDeactivatedUsers
        ? true
        : user.userStatus !== UserStatus.Inactive;
    });

    return data;
  }, [includeDeactivatedUsers, membersData?.users]);
}

// Async version of useGetAllUsers
export function useAsyncAccountUsers() {
  const userOption = useCallback(
    (user: User): CustomOption => ({
      label: fullName(user),
      value: user.userId,
      imageUrl: getThumbnailUrl(user.photoUrl),
      userStatus: user.userStatus,
      extra: {
        id: user.userId,
        type: 'user',
        role: user.role,
        userType: user.userType,
        company: user.company,
      },
    }),
    [],
  );

  // Fetch initial data at account-level
  const { data: membersData, isLoading } = useQuery(
    ['account-users'],
    async () => {
      const data = await MembersAPI.fetchPaginatedMembersData({
        projectId: -1, // Account-level: fetch ALL users
        userStatus: [UserStatus.Active, UserStatus.Invited],
        startRow: 0,
        endRow: 50,
      });

      return data;
    },
  );

  // Filter users based on basic rules
  const filterUsers = useCallback((users: User[]): User[] => {
    return users.filter((user) => {
      // Rule 1: Filter out External users
      if (user.userType === UserType.External) {
        return false;
      }

      // Rule 2: Only Active or Invited (custom fields don't show inactive)
      if (user.userStatus === UserStatus.Inactive) {
        return false;
      }

      // Rule 3: Filter out API key users (UUID emails without @)
      if (user.emailId && !user.emailId.includes('@')) {
        return false;
      }

      return true;
    });
  }, []);

  // Default options from initial fetch
  const defaultOptions: CustomOption[] = useMemo(() => {
    if (!membersData) {
      return [];
    }

    // Combine all account users (not project-specific)
    const allUsers = [
      ...membersData.inProjectUsers,
      ...membersData.notInProjectUsers,
    ];

    return filterUsers(allUsers).map(userOption);
  }, [membersData, filterUsers, userOption]);

  // Async load options for search
  const loadOptions = useCallback(
    async (inputValue: string): Promise<CustomOption[]> => {
      try {
        const data = await MembersAPI.fetchPaginatedMembersData({
          projectId: -1, // Account-level: fetch ALL users
          searchString: inputValue,
          userStatus: [UserStatus.Active, UserStatus.Invited],
          startRow: 0,
          endRow: 100,
        });

        // Combine all account users
        const allUsers = [...data.inProjectUsers, ...data.notInProjectUsers];

        return filterUsers(allUsers).map(userOption);
      } catch (error) {
        return [];
      }
    },
    [filterUsers, userOption],
  );

  return {
    defaultOptions,
    loadOptions,
    isLoading,
  };
}

export function useGetAllActiveUsersMap(): Record<number, User> {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const users = (membersData?.users ?? []).filter(
      (user) => user.userStatus !== UserStatus.Inactive,
    );

    return keyBy(users, 'userId');
  }, [membersData?.users]);
}

export function useGetAllNativeUsersMap(): Record<number, User> {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const users = (membersData?.users ?? []).filter(
      (user) => user.userType === UserType.Native,
    );

    return keyBy(users, 'userId');
  }, [membersData?.users]);
}

export function useGetAllNativeAndPartnerUsersMap(): Record<number, User> {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const users = (membersData?.users ?? []).filter(
      (user) => user.userType !== UserType.External,
    );

    return keyBy(users, 'userId');
  }, [membersData?.users]);
}

export function useGetAllNativeActiveUsersMap(): Record<number, User> {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const users = (membersData?.users ?? []).filter(
      (user) =>
        user.userStatus !== UserStatus.Inactive &&
        user.userType === UserType.Native,
    );

    return keyBy(users, 'userId');
  }, [membersData?.users]);
}

const useFilterForUserInviteableInProject = () => {
  const { user: currentUser } = useUser();

  const getFilterUserInviteableInProject = useCallback(
    (
      project: ProjectWithPartners | undefined,
      projectLight: ProjectInUserProfile,
      isPrivate: boolean,
      options: ProjectMembersOptions = {
        includeRestricedPartnersMembers: true,
      },
    ) => {
      const customerCompanyId = projectLight?.customer.companyId;
      const currentUserCompanyFromProjectPartners =
        currentUser.userType === UserType.Partner
          ? (project?.partners ?? []).find(
              (partner) => partner.companyId === currentUser.company.companyId,
            )
          : undefined;
      const partnersInProject = (project?.partners ?? []).map(
        ({ companyId }) => companyId,
      );
      const restricedPartnerCompanyIdsInProject = (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map(({ companyId }) => companyId);
      const partnersWhoCanAccessPrivateResources = (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map(({ companyId }) => companyId);

      const canBeInvitedByVendorOnPrivateResource = (user: User) =>
        user.userType === UserType.Native ||
        (user.userType === UserType.Partner &&
          partnersWhoCanAccessPrivateResources.includes(
            user.company.companyId,
          ));
      const canBeInvitedByVendorOnSharedesource = (user: User) =>
        user.userType === UserType.Native ||
        (user.userType === UserType.Partner &&
          partnersInProject.includes(user.company.companyId) &&
          (options.includeRestricedPartnersMembers === true ||
            !restricedPartnerCompanyIdsInProject.includes(
              user.company.companyId,
            ))) ||
        (user.userType === UserType.External &&
          user.company.companyId === customerCompanyId);

      const canBeInvitedByVendor = (user: User) =>
        isPrivate
          ? canBeInvitedByVendorOnPrivateResource(user)
          : canBeInvitedByVendorOnSharedesource(user);
      const canBeInvitedByCustomer = (user: User) =>
        isPrivate ? false : user.company.companyId === customerCompanyId;

      const canBeInvitedByPartner = (user: User) => {
        if (currentUserCompanyFromProjectPartners == null) {
          return false;
        }
        if (
          isPrivate &&
          currentUserCompanyFromProjectPartners.accessLevel
            ?.partnerPermissionSetName !== PartnerPermissionName.Permissive
        ) {
          return false;
        }

        return user.company.companyId === currentUser.company.companyId;
      };
      const canBeInvitedByCurrentUser = (user: User) => {
        switch (currentUser.userType) {
          case UserType.Native:
            return canBeInvitedByVendor(user);
          case UserType.CustomerPartner:
          case UserType.External:
          case UserType.CustomerPortalBot:
            return canBeInvitedByCustomer(user);
          case UserType.Partner:
            return canBeInvitedByPartner(user);
          default:
            return unreachable(currentUser.userType as never);
        }
      };

      return canBeInvitedByCurrentUser;
    },
    [currentUser.company.companyId, currentUser.userType],
  );

  return {
    getFilterUserInviteableInProject,
  };
};

// if UserType === NATIVE then load all native users who are not in this project
// if UserType === EXTERNAL then load all cusatomer users who are not in this project
export function useGetAllMembersNotInProject({
  projectId,
  isPrivate = false,
  options = {
    includeRestricedPartnersMembers: true,
    dontIncludeCustomers: false,
  },
  ignoreRbacRules = false,
}: {
  projectId?: number;
  isPrivate?: boolean;
  options?: ProjectMembersOptions;
  ignoreRbacRules?: boolean;
}): { data: User[]; isLoading: boolean } {
  const { data: project } = useProjectDetail(ProjectId(projectId ?? -1));
  const projectResource = useProjectAsRBACResource(projectId);
  const customerCompanyId = project?.customer.companyId;
  const { user: currentUser } = useUser();
  const { data: membersData, isLoading: isMembersLoading } =
    useMembersV2TransformedQuery();
  const currentUserCompanyFromProjectPartners = useMemo(
    () =>
      currentUser.userType === UserType.Partner ||
      currentUser.userType === UserType.CustomerPartner
        ? (project?.partners ?? []).find(
            (partner) => partner.companyId === currentUser.company.companyId,
          )
        : undefined,
    [currentUser.company.companyId, currentUser.userType, project?.partners],
  );
  const partnersInProject = useMemo(
    () => (project?.partners ?? []).map(({ companyId }) => companyId),
    [project?.partners],
  );

  const restricedPartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map(({ companyId }) => companyId),
    [project?.partners],
  );

  const partnersWhoCanAccessPrivateResources = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map(({ companyId }) => companyId),
    [project?.partners],
  );

  const canBeInvitedByVendorOnPrivateResource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      ((user.userType === UserType.Partner ||
        user.userType === UserType.CustomerPartner) &&
        partnersWhoCanAccessPrivateResources.includes(user.company.companyId)),
    [partnersWhoCanAccessPrivateResources],
  );
  const canBeInvitedByVendorOnSharedesource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      ((user.userType === UserType.Partner ||
        user.userType === UserType.CustomerPartner) &&
        partnersInProject.includes(user.company.companyId) &&
        (options.includeRestricedPartnersMembers === true ||
          !restricedPartnerCompanyIdsInProject.includes(
            user.company.companyId,
          ))) ||
      (user.userType === UserType.External &&
        user.company.companyId === customerCompanyId),
    [
      customerCompanyId,
      options.includeRestricedPartnersMembers,
      partnersInProject,
      restricedPartnerCompanyIdsInProject,
    ],
  );

  const canBeInvitedByVendor = useCallback(
    (user: User) =>
      isPrivate
        ? canBeInvitedByVendorOnPrivateResource(user)
        : canBeInvitedByVendorOnSharedesource(user),

    [
      canBeInvitedByVendorOnPrivateResource,
      canBeInvitedByVendorOnSharedesource,
      isPrivate,
    ],
  );
  const canBeInvitedByCustomer = useCallback(
    (user: User) =>
      isPrivate ? false : user.company.companyId === customerCompanyId,
    [customerCompanyId, isPrivate],
  );
  const canBeInvitedByCustomerPartner = useCallback(
    (user: User) =>
      isPrivate
        ? false
        : user.company.companyId === currentUser.company.companyId,
    [isPrivate, currentUser],
  );
  const canBeInvitedByPartner = useCallback(
    (user: User) => {
      if (currentUserCompanyFromProjectPartners == null) {
        return false;
      }
      if (
        isPrivate &&
        currentUserCompanyFromProjectPartners.accessLevel
          ?.partnerPermissionSetName !== PartnerPermissionName.Permissive
      ) {
        return false;
      }

      return user.company.companyId === currentUser.company.companyId;
    },
    [
      currentUser.company.companyId,
      currentUserCompanyFromProjectPartners,
      isPrivate,
    ],
  );
  const { hasAccessToResource } = useWithAccess();

  const {
    canInviteCustomerUsers,
    canInviteVendorUsers,
    canInvitePartnerUsers,
  } = useMemo(
    () => ({
      canInviteVendorUsers: hasAccessToResource({
        rule: 'PROJECT_MEMBER:CREATE',
        resourceData: projectResource,
        actionScope: ['NATIVE'],
      }),
      canInviteCustomerUsers: hasAccessToResource({
        rule: 'PROJECT_MEMBER:CREATE',
        resourceData: projectResource,
        actionScope: ['EXTERNAL'],
      }),
      canInvitePartnerUsers: hasAccessToResource({
        rule: 'PROJECT_MEMBER:CREATE',
        resourceData: projectResource,
        actionScope: ['PARTNER'],
      }),
    }),
    [hasAccessToResource, projectResource],
  );
  const hasAccessToInviteThisUser = useCallback(
    (user: User) => {
      if (ignoreRbacRules) {
        return true;
      }
      switch (user.userType) {
        case UserType.Native:
          return canInviteVendorUsers;
        case UserType.CustomerPartner:
        case UserType.External:
          return canInviteCustomerUsers;
        case UserType.Partner:
          return canInvitePartnerUsers;
        case UserType.CustomerPortalBot:
        default:
          return unreachable(user.userType as never);
      }
    },
    [
      canInviteCustomerUsers,
      canInvitePartnerUsers,
      canInviteVendorUsers,
      ignoreRbacRules,
    ],
  );
  const canBeInvitedByCurrentUser = useCallback(
    (user: User) => {
      switch (currentUser.userType) {
        case UserType.Native:
          return canBeInvitedByVendor(user) && hasAccessToInviteThisUser(user);
        case UserType.External:
        case UserType.CustomerPortalBot:
          return (
            canBeInvitedByCustomer(user) && hasAccessToInviteThisUser(user)
          );
        case UserType.Partner:
          return canBeInvitedByPartner(user);
        case UserType.CustomerPartner:
          return canBeInvitedByCustomerPartner(user);
        default:
          return unreachable(currentUser.userType as never);
      }
    },
    [
      currentUser.userType,
      canBeInvitedByVendor,
      hasAccessToInviteThisUser,
      canBeInvitedByCustomer,
      canBeInvitedByPartner,
      canBeInvitedByCustomerPartner,
    ],
  );

  const conditionalFilter = useCallback(
    (user: User) => {
      if (!projectId) {
        return false;
      }

      return (
        !user.projectIds?.includes(projectId) && canBeInvitedByCurrentUser(user)
      );
    },
    [canBeInvitedByCurrentUser, projectId],
  );

  const data = useMemo(() => {
    if (!projectId) {
      return [];
    }

    // using membersData from cache because indexedDb is taking 20 seconds to return results
    return (membersData?.users ?? []).filter(conditionalFilter);
  }, [conditionalFilter, projectId, membersData]);

  return { data: data || [], isLoading: isMembersLoading };
}

export function useGetAllMembersByCompany(companyId: number): User[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    if (!companyId) {
      return [];
    }
    const data = (membersData?.users ?? []).filter(
      (user) => user.company.companyId === companyId,
    );

    return data;
  }, [companyId, membersData?.users]);
}

export function useGetAllTeamsFromIndexedDb(): Team[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = membersData?.teams ?? [];

    return data;
  }, [membersData?.teams]);
}

export function useGetAllTeamsFromIndexedDbConditionally(
  projectId?: number,
): Team[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.teams ?? []).filter((team) => {
      if (projectId) {
        return !!team?.projectIds?.includes?.(projectId);
      }

      return !!team?.accountLevelTeam;
    });

    return data;
  }, [membersData?.teams, projectId]);
}

/**
 * @deprecated
 */
export function useGetUserFromIndexedDb(userId?: number): User {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).find(
      (user) => user.userId === userId,
    );

    return data ?? ({} as User);
  }, [membersData?.users, userId]);
}

export const useGetUserFromProjectMembers = (
  projectId: number,
  userId: number,
): User => {
  const { data: projectMembers } = useProjectMembersQuery([
    'project_id',
    projectId,
  ]);

  return useMemo(() => {
    const data = projectMembers?.userList?.find(
      (user) => user.userId === userId,
    );

    return data ?? ({} as User);
  }, [projectMembers, userId]);
};

export function useGetProjectIdsOfCurrentUserFromIndexedDb(): number[] {
  const {
    user: { userId },
  } = useUser();
  // Using query cache instead of IndexedDB, since useLiveQuery is causing unnecessary rerenders
  const { data: membersData } = useMembersV2TransformedQuery();

  const data = useMemo(() => {
    return (membersData?.users ?? []).find?.((x) => x.userId === userId);
  }, [userId, membersData?.users]);

  return data?.projectIds || [];
}

export function useGetProjectIdsOfUserFromIndexedDb(userId: number): number[] {
  const data = useGetUserFromIndexedDb(userId);

  return data?.projectIds || [];
}

export function useGetUsersFromIndexedDb(userIds: number[]): User[] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.users ?? []).filter((user) =>
      userIds.includes(user.userId),
    );

    return data;
  }, [membersData?.users, userIds]);
}

export function useGetExteralUsersOfCompanyFromIndexedDb(
  companyIds: number[],
): User[] | undefined {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(
    () =>
      (membersData?.users ?? []).filter(
        (user) =>
          user.userType === UserType.External &&
          companyIds.includes(user.company.companyId),
      ),
    [companyIds, membersData?.users],
  );
}

export function useGetTeamsFromIndexedDb(teamIds: number[]): Team[] | [] {
  const { data: membersData } = useMembersV2TransformedQuery();

  return useMemo(() => {
    const data = (membersData?.teams ?? []).filter((team) =>
      teamIds.includes(team.teamId),
    );

    return data;
  }, [membersData?.teams, teamIds]);
}

export function useFullName(userId?: number): string {
  const user = useGetUserFromIndexedDb(userId);

  return fullName(user as User);
}

async function getAllColumnOrderFromIndexedDb(
  orderType: OrderType,
): Promise<Order[]> {
  const data = await db.columnOrder.where({ orderType }).toArray();

  return data;
}

export async function getColumnOrderFromIndexedDb(key: string): Promise<Order> {
  const data = await db.columnOrder.where({ key }).toArray();

  return data[0];
}

async function doesColumnOrderExist(key: string): Promise<boolean> {
  try {
    const data = await db.columnOrder.get(key);

    return data !== undefined;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return false;
  }
}

export async function addUpdateColumnOrderIndexedDb(
  key: string,
  viewId: number,
  columnOrder: ColumnOrder[],
  orderType: OrderType,
): Promise<string> {
  const doesExist = await doesColumnOrderExist(key);

  let payload: Order = {
    key,
    viewId,
    columnOrder,
    orderType,
  };

  if (doesExist) {
    const oldData = await db.columnOrder.get(key);

    payload = { ...payload, createdAt: oldData?.createdAt };
  } else {
    payload = { ...payload, createdAt: new Date().getTime() };
  }
  const data = await db.columnOrder.put(payload, key);

  return data;
}

export async function deleteColumnOrderFromIndexedDb<T>(
  orderType: OrderType,
  currentListViews: Views<T>,
): Promise<void> {
  const idbList = await getAllColumnOrderFromIndexedDb(orderType);

  if (idbList.length === 0 && currentListViews.ids[0] !== 'just') {
    return;
  }
  const deletedViewKeys = idbList
    .filter(
      (x) =>
        currentListViews.ids[0] === 'just' &&
        x.viewId >= 0 &&
        !currentListViews?.ids?.[1]?.includes(x.viewId),
    )
    .map((item) => item.key);

  if (deletedViewKeys.length > 0) {
    await db.columnOrder.bulkDelete(deletedViewKeys);
  }
}

export async function getFormResponseDraftFromIndexedDb(
  formInstance: number,
): Promise<FormResponseDraft> {
  const data = await db.formResponseDraft.where({ formInstance }).toArray();

  return data[0];
}

export async function addUpdateFormResponseDraftIndexedDb(
  formInstance: number,
  response: FormResponse,
): Promise<number> {
  const data = await db.formResponseDraft.put(
    {
      response,
      timestamp: moment().valueOf(),
      formInstance,
    },
    formInstance,
  );

  return data;
}

export async function deleteFormResponseFromIndexedDb(
  formInstance: number,
): Promise<void> {
  return db.formResponseDraft.delete(formInstance);
}
interface ProjectUsersType {
  inProjectUsers: User[];
  notInProjectUsers: User[];
}

export const useMembersInAndNotInProjects = ({
  projectIds,
  isPrivate,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): ProjectUsersType => {
  // TODO @Muskaan - debounce the computation
  const { data: membersData } = useMembersV2TransformedQuery();
  const { data: projectsInitData } = useProjectsFromInitData();
  const activeUsers = membersData?.users;
  const { getFilterUserInviteableInProject } =
    useFilterForUserInviteableInProject();
  const { data: projectLookup, isLoading } =
    useProjectPartnersMappingQuery(projectIds);

  return useMemo(() => {
    if (isLoading || projectLookup == null || projectsInitData == null) {
      return {
        inProjectUsers: [],
        notInProjectUsers: [],
      };
    }
    const usersBelongingToProjects: Record<EntityId, ProjectUsersType> =
      projectIds.reduce((previous, projectId) => {
        const projectWithpartner = projectLookup?.[projectId];
        const permissivePartnerCompanyIdsInProject = (
          projectWithpartner?.partners ?? []
        )
          .filter(
            (partner) =>
              partner.accessLevel?.partnerPermissionSetName ===
              PartnerPermissionName.Permissive,
          )
          .map((partner) => partner.companyId);
        const inProjectUser = (user: User) =>
          user.projectIds?.includes(projectId);
        const permissivePartnerUser = (user: User) =>
          permissivePartnerCompanyIdsInProject.includes(user.company.companyId);
        const assignableInPrivateResource = (user: User) =>
          user.userType === UserType.Native || permissivePartnerUser(user);
        const filterFunctionForInviteableUser =
          getFilterUserInviteableInProject(
            projectWithpartner,
            projectsInitData.byId[projectId],
            isPrivate,
          );
        const inProjectUsers = (activeUsers ?? []).filter(
          (user) =>
            inProjectUser(user) &&
            (isPrivate ? assignableInPrivateResource(user) : true),
        );
        const notInProjectUsers = (activeUsers ?? []).filter(
          (user) =>
            !inProjectUser(user) && filterFunctionForInviteableUser(user),
        );

        previous[projectId] = {
          inProjectUsers,
          notInProjectUsers,
        };

        return previous;
      }, {});

    const usersInProjects = projectIds.reduce((previous, projectId) => {
      const match = usersBelongingToProjects[projectId];

      if (match == null) {
        return previous;
      }

      return intersectionBy(
        previous,
        unionBy(match.inProjectUsers, match.notInProjectUsers, 'userId'),
        'userId',
      );
    }, activeUsers ?? []);

    const inProjectUsers = projectIds.reduce((previous, projectId) => {
      return intersectionBy(
        previous,
        usersBelongingToProjects[projectId]?.inProjectUsers ?? [],
        'userId',
      );
    }, usersInProjects);

    const notInProjectUsers = differenceBy(
      usersInProjects,
      inProjectUsers,
      'userId',
    );

    return {
      inProjectUsers,
      notInProjectUsers,
    };
  }, [
    activeUsers,
    getFilterUserInviteableInProject,
    isLoading,
    isPrivate,
    projectIds,
    projectLookup,
    projectsInitData,
  ]);
};

export async function getTemplateDraftFromIndexedDb(
  templateId: TemplateId,
): Promise<TemplateDraft> {
  const data = await db.templateDraft.where({ templateId }).toArray();
  const template = data?.[0];

  if (template?.templateType === TemplateObjectType.Project) {
    return {
      ...template,
      content: migrations.addEntityOrderToProjectTemplatePhases(
        template.content as ProjectTemplateApi,
      ),
    };
  }

  return template;
}

export async function addUpdateTemplateDraftIndexedDb(
  templateId: TemplateId,
  content: ProjectTemplateApi | DocumentTemplate,
  templateType: TemplateObjectType,
): Promise<TemplateId> {
  const data = await db.templateDraft.put(
    {
      content,
      timestamp: moment().valueOf(),
      templateId,
      templateType,
    },
    templateId,
  );

  return data;
}

export async function deleteTemplateFromIndexedDb(
  templateIds: TemplateId[],
): Promise<void> {
  return db.templateDraft.bulkDelete(templateIds);
}

export async function clearColumnOrderTableFromIndexedDb() {
  await db.columnOrder.clear();
}
